import {stripHTMLTags} from '@edume/magnificent';

import {sendSearchedMediaEvent} from '../../tracking/amplitudeEvents/mediaLibraryEvents';
import {EDUME_LIBRARY, GROUP_LIBRARY} from '../../tracking/trackingProps';
import {injectEndpoints} from '../api/apiSlice';
import {IMAGE_LIBRARY} from '../api/tagTypes';
import {getSelectedGroupId} from '../team/teamSelectors';
import {createSelector} from '../util';

export type Image = {
  id: number;
  mediaLibraryAssetId: number;
  groupId: number;
  url: string;
  filename: string;
  width: number;
  height: number;
};

type ImageLibraryApiResponse = {
  edumeImages: Image[];
  groupImages: Image[];
};

type ImageLibraryTransformedResponse = {
  edumeImageMediaLibraryAssetIds: number[];
  groupImageMediaLibraryAssetIds: number[];
  entities: Record<number, Image>;
};

type SearchImagesResponse = {
  searchRunId: string;
  images: Image[];
};

const extendedApi = injectEndpoints({
  endpoints: (build) => ({
    getImagesFromLibrary: build.query<ImageLibraryTransformedResponse, number>({
      query: (groupId) => ({url: `groups/${groupId}/images`}),
      transformResponse: (res: ImageLibraryApiResponse) => {
        const entities = Object.fromEntries(
          res.groupImages
            .concat(res.edumeImages)
            .map((img) => [img.mediaLibraryAssetId, img])
        );

        return {
          edumeImageMediaLibraryAssetIds: res.edumeImages.map(
            (image) => image.mediaLibraryAssetId
          ),
          groupImageMediaLibraryAssetIds: res.groupImages.map(
            (image) => image.mediaLibraryAssetId
          ),
          entities,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...[
                ...result.edumeImageMediaLibraryAssetIds,
                ...result.groupImageMediaLibraryAssetIds,
              ].map((id) => ({
                type: IMAGE_LIBRARY,
                id: id,
              })),
              {type: IMAGE_LIBRARY, id: 'LIST'},
            ]
          : [{type: IMAGE_LIBRARY, id: 'LIST'}],
    }),
    searchImages: build.query<
      SearchImagesResponse,
      {
        groupId: number | null;
        searchQuery: string;
        trackingData: {
          courseId?: number;
          lessonId?: number;
          guideId?: number | string | null;
        };
      }
    >({
      query: ({groupId, searchQuery}) => {
        const encodedQuery = encodeURIComponent(stripHTMLTags(searchQuery));
        return groupId
          ? `groups/${groupId}/images/search?query=${encodedQuery}`
          : `imageLibrary/search?query=${encodedQuery}`;
      },
      async onQueryStarted(
        {groupId, searchQuery, trackingData},
        {queryFulfilled}
      ) {
        try {
          const {data: searchResults} = await queryFulfilled;
          const {courseId, lessonId, guideId} = trackingData;
          const source = groupId ? GROUP_LIBRARY : EDUME_LIBRARY;

          sendSearchedMediaEvent({
            courseId,
            lessonId,
            guideId,
            source,
            searchQuery,
            numberOfResults: searchResults.images.length || 0,
          });
        } catch (error) {
          console.error('Search query failed:', error);
        }
      },
      providesTags: () => [{type: IMAGE_LIBRARY, id: 'LIST'}],
    }),
    searchAllImages: build.query<
      SearchImagesResponse,
      {
        groupId: number;
        searchQuery: string;
      }
    >({
      query: ({groupId, searchQuery}) =>
        `groups/${groupId}/images/search?query=${encodeURIComponent(
          stripHTMLTags(searchQuery)
        )}&includeEdumeImages=true`,
      providesTags: () => [{type: IMAGE_LIBRARY, id: 'LIST'}],
    }),
    addImageToLibrary: build.mutation<
      unknown,
      {
        url: string;
        filename: string;
        groupId: number;
        width?: number;
        height?: number;
      }
    >({
      queryFn: async (
        {url, filename, groupId, width, height},
        api,
        extraOptions,
        baseQuery
      ) =>
        baseQuery({
          url: '/imageLibrary',
          method: 'POST',
          body: {url, filename, groupId, width, height},
        }),
      invalidatesTags: [{type: IMAGE_LIBRARY, id: 'LIST'}],
    }),
    deleteFromImageLibrary: build.mutation<
      void,
      Record<'groupId' | 'imageId', number>
    >({
      query: ({groupId, imageId}) => ({
        url: `/groups/${groupId}/images/${imageId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{type: IMAGE_LIBRARY, id: 'LIST'}],
    }),
    trackEdumeImageLibraryUse: build.mutation<
      void,
      Record<'groupId' | 'imageId', number>
    >({
      query: ({groupId, imageId}) => ({
        url: '/trackEdumeImageLibraryUse',
        method: 'POST',
        body: {groupId, imageId},
      }),
    }),
  }),
});

export const {
  useGetImagesFromLibraryQuery,
  useSearchImagesQuery,
  useLazySearchAllImagesQuery,
  useAddImageToLibraryMutation,
  useDeleteFromImageLibraryMutation,
  useTrackEdumeImageLibraryUseMutation,
} = extendedApi;

const createGetImageLibrarySelector = createSelector(
  [(state) => getSelectedGroupId(state)],
  (groupId) => extendedApi.endpoints.getImagesFromLibrary.select(groupId)
);

export const selectImageByMediaLibraryAssetId = createSelector(
  [
    (state) => state,
    (state, mediaLibraryAssetId: number) => mediaLibraryAssetId,
    (state) => createGetImageLibrarySelector(state),
  ],
  (state, mediaLibraryAssetId, selectImageEntityAdapter) =>
    selectImageEntityAdapter(state).data?.entities[mediaLibraryAssetId]
);
